import { Check, ChevronDown } from 'lucide-react';
import type { ReactNode } from 'react';
import type {
  DateBound,
  DateFilter,
  DateUnit,
} from '@/components/tree-dnd/tree-dnd-types';
import { DateBoundControl } from '@/components/tree-dnd/tree-dnd-conditions/date-bound-control';
import {
  createFilterForSelection,
  detectDateFilterSelection,
  notWithinLastFilter,
  type DateFilterMenuSelection,
  withinLastFilter,
} from '@/components/tree-dnd/tree-dnd-conditions/date-filter-utils';
import { Button } from '@/components/ui/button';
import { DateInputPicker } from '@/components/ui/date-input-picker';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useI18nContext } from '@/i18n/i18n-react';
import { cn } from '@/lib/utils';

interface DateFilterControlProps {
  value: DateFilter;
  onChange: (value: DateFilter) => void;
}

function MenuItemRow({
  selected,
  children,
  onSelect,
}: {
  selected: boolean;
  children: ReactNode;
  onSelect: () => void;
}) {
  return (
    <DropdownMenuItem className="gap-2" onSelect={onSelect}>
      <Check
        className={cn('h-4 w-4', selected ? 'opacity-100' : 'opacity-0')}
      />
      {children}
    </DropdownMenuItem>
  );
}

function WithinLastEditors({
  amount,
  unit,
  onChange,
}: {
  amount: number;
  unit: DateUnit;
  onChange: (amount: number, unit: DateUnit) => void;
}) {
  const { LL } = useI18nContext();

  return (
    <div className="flex items-center gap-2">
      <span className="text-sm text-muted-foreground">
        {LL.audiences.filters.date.withinLastPrefix()}
      </span>
      <Input
        type="number"
        min={1}
        className="h-8 w-20"
        value={amount}
        onChange={(event) => {
          const next = Math.max(1, Number(event.target.value) || 1);
          onChange(next, unit);
        }}
      />
      <Select
        value={unit}
        onValueChange={(next) => onChange(amount, next as DateUnit)}
      >
        <SelectTrigger className="h-8 w-auto min-w-28">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="days">
            {LL.audiences.filters.date.days()}
          </SelectItem>
          <SelectItem value="weeks">
            {LL.audiences.filters.date.weeks()}
          </SelectItem>
          <SelectItem value="months">
            {LL.audiences.filters.date.months()}
          </SelectItem>
          <SelectItem value="years">
            {LL.audiences.filters.date.years()}
          </SelectItem>
        </SelectContent>
      </Select>
      <span className="text-sm text-muted-foreground">
        {LL.audiences.filters.date.beforeSend()}
      </span>
    </div>
  );
}

export function DateFilterControl({ value, onChange }: DateFilterControlProps) {
  const { LL } = useI18nContext();
  const selection = detectDateFilterSelection(value);

  const labelForSelection = (key: DateFilterMenuSelection): string => {
    switch (key) {
      case 'anytime':
        return LL.audiences.filters.date.anytime();
      case 'onSendDay':
        return LL.audiences.filters.date.onSendDay();
      case 'dayBeforeSend':
        return LL.audiences.filters.date.dayBeforeSend();
      case 'last7Days':
        return LL.audiences.filters.date.last7Days();
      case 'last30Days':
        return LL.audiences.filters.date.last30Days();
      case 'withinLast':
        return LL.audiences.filters.date.withinLast();
      case 'notLast7Days':
        return LL.audiences.filters.date.notLast7Days();
      case 'notLast30Days':
        return LL.audiences.filters.date.notLast30Days();
      case 'notWithinLast':
        return LL.audiences.filters.date.notWithinLast();
      case 'customRelative':
        return LL.audiences.filters.date.customRelative();
      case 'customAbsolute':
        return LL.audiences.filters.date.customAbsolute();
      case 'customWindow':
        return LL.audiences.filters.date.customBetween();
    }
  };

  const select = (next: DateFilterMenuSelection) => {
    onChange(createFilterForSelection(next, value));
  };

  const withinAmountUnit = (): { amount: number; unit: DateUnit } | null => {
    if (value.type !== 'window') return null;
    if (selection === 'withinLast' && value.from.type === 'relative') {
      return { amount: value.from.offset, unit: value.from.unit };
    }
    if (
      (selection === 'notWithinLast' ||
        selection === 'notLast7Days' ||
        selection === 'notLast30Days') &&
      value.to.type === 'relative'
    ) {
      return { amount: value.to.offset, unit: value.to.unit };
    }
    if (selection === 'withinLast' || selection === 'notWithinLast') {
      return { amount: 7, unit: 'days' };
    }
    return null;
  };

  const editors = withinAmountUnit();

  return (
    <div className="flex flex-col gap-3">
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            type="button"
            variant="outline"
            className="h-8 w-full justify-between px-3 font-normal"
          >
            <span className="truncate">{labelForSelection(selection)}</span>
            <ChevronDown className="h-4 w-4 opacity-50" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="w-72">
          <DropdownMenuGroup>
            <MenuItemRow
              selected={selection === 'anytime'}
              onSelect={() => select('anytime')}
            >
              {LL.audiences.filters.date.anytime()}
            </MenuItemRow>
            <MenuItemRow
              selected={selection === 'onSendDay'}
              onSelect={() => select('onSendDay')}
            >
              {LL.audiences.filters.date.onSendDay()}
            </MenuItemRow>
            <MenuItemRow
              selected={selection === 'dayBeforeSend'}
              onSelect={() => select('dayBeforeSend')}
            >
              {LL.audiences.filters.date.dayBeforeSend()}
            </MenuItemRow>
            <MenuItemRow
              selected={selection === 'last7Days'}
              onSelect={() => select('last7Days')}
            >
              {LL.audiences.filters.date.last7Days()}
            </MenuItemRow>
            <MenuItemRow
              selected={selection === 'last30Days'}
              onSelect={() => select('last30Days')}
            >
              {LL.audiences.filters.date.last30Days()}
            </MenuItemRow>
            <MenuItemRow
              selected={selection === 'withinLast'}
              onSelect={() => select('withinLast')}
            >
              {LL.audiences.filters.date.withinLast()}
            </MenuItemRow>
          </DropdownMenuGroup>
          <DropdownMenuSeparator />
          <DropdownMenuGroup>
            <MenuItemRow
              selected={selection === 'notLast7Days'}
              onSelect={() => select('notLast7Days')}
            >
              {LL.audiences.filters.date.notLast7Days()}
            </MenuItemRow>
            <MenuItemRow
              selected={selection === 'notLast30Days'}
              onSelect={() => select('notLast30Days')}
            >
              {LL.audiences.filters.date.notLast30Days()}
            </MenuItemRow>
            <MenuItemRow
              selected={selection === 'notWithinLast'}
              onSelect={() => select('notWithinLast')}
            >
              {LL.audiences.filters.date.notWithinLast()}
            </MenuItemRow>
          </DropdownMenuGroup>
          <DropdownMenuSeparator />
          <DropdownMenuGroup>
            <DropdownMenuLabel>
              {LL.audiences.filters.date.custom()}
            </DropdownMenuLabel>
            <MenuItemRow
              selected={selection === 'customRelative'}
              onSelect={() => select('customRelative')}
            >
              {LL.audiences.filters.date.customRelative()}
            </MenuItemRow>
            <MenuItemRow
              selected={selection === 'customAbsolute'}
              onSelect={() => select('customAbsolute')}
            >
              {LL.audiences.filters.date.customAbsolute()}
            </MenuItemRow>
            <MenuItemRow
              selected={selection === 'customWindow'}
              onSelect={() => select('customWindow')}
            >
              {LL.audiences.filters.date.customBetween()}
            </MenuItemRow>
          </DropdownMenuGroup>
        </DropdownMenuContent>
      </DropdownMenu>

      {selection === 'withinLast' && editors ? (
        <WithinLastEditors
          amount={editors.amount}
          unit={editors.unit}
          onChange={(amount, unit) => onChange(withinLastFilter(amount, unit))}
        />
      ) : null}

      {selection === 'notWithinLast' && editors ? (
        <WithinLastEditors
          amount={editors.amount}
          unit={editors.unit}
          onChange={(amount, unit) =>
            onChange(notWithinLastFilter(amount, unit))
          }
        />
      ) : null}

      {value.type === 'relative' ? (
        <div className="flex flex-col gap-3">
          <Select
            value={value.direction}
            onValueChange={(direction) =>
              onChange({
                ...value,
                direction: direction as 'before' | 'after',
              })
            }
          >
            <SelectTrigger className="h-8 w-full">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="before">
                {LL.audiences.filters.date.directionBefore()}
              </SelectItem>
              <SelectItem value="after">
                {LL.audiences.filters.date.directionAfter()}
              </SelectItem>
            </SelectContent>
          </Select>
          <DateBoundControl
            value={value.date}
            onChange={(date: DateBound) => onChange({ ...value, date })}
          />
        </div>
      ) : null}

      {value.type === 'absolute' ? (
        <div className="flex flex-col gap-3">
          <Select
            value={value.direction}
            onValueChange={(direction) =>
              onChange({
                ...value,
                direction: direction as 'before' | 'after',
              })
            }
          >
            <SelectTrigger className="h-8 w-full">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="before">
                {LL.audiences.filters.date.directionBefore()}
              </SelectItem>
              <SelectItem value="after">
                {LL.audiences.filters.date.directionAfter()}
              </SelectItem>
            </SelectContent>
          </Select>
          <DateInputPicker
            value={value.date}
            placeholder={LL.audiences.filters.date.pickDate()}
            onChange={(date) => onChange({ ...value, date })}
          />
        </div>
      ) : null}

      {selection === 'customWindow' && value.type === 'window' ? (
        <div className="flex flex-col gap-4">
          <div className="space-y-1.5">
            <p className="text-xs font-medium text-muted-foreground">
              {LL.audiences.filters.date.from()}
            </p>
            <DateBoundControl
              value={value.from}
              allowEpoch
              onChange={(from) => onChange({ ...value, from })}
            />
          </div>
          <div className="space-y-1.5">
            <p className="text-xs font-medium text-muted-foreground">
              {LL.audiences.filters.date.to()}
            </p>
            <DateBoundControl
              value={value.to}
              allowEpoch
              onChange={(to) => onChange({ ...value, to })}
            />
          </div>
        </div>
      ) : null}
    </div>
  );
}
